/** Session-cookie fetch wrappers for Worker routes (GOAL_HW_COLLAB_LOOP §8). */

import { parseFindingsEnvelope, type FindingsEnvelope } from "./findings-envelope.ts";
import type { MaterialKind, MaterialRow, MaterialUploadResult } from "./materials.ts";

export type { AgentFinding, FindingsEnvelope } from "./findings-envelope.ts";
export { parseFindingsEnvelope };
export type { MaterialKind, MaterialRow, MaterialUploadResult } from "./materials.ts";
export { MATERIAL_KINDS } from "./materials.ts";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export type Role = "owner" | "editor" | "viewer" | "hardware" | "firmware" | "review" | "guest";
export type InviteCapability = "edit" | "view";
export type InviteRole = "editor" | "viewer";

export type SessionUser = {
  id: string;
  email: string;
  name: string;
  image?: string | null;
};

export async function fetchSessionUser(): Promise<SessionUser | null> {
  const res = await fetch("/api/session", { credentials: "include" });
  if (!res.ok) return null;
  const body = (await res.json()) as { user?: SessionUser | null };
  const user = body?.user;
  if (!user?.id || !user.email) return null;
  return user;
}

export async function signOutSession(): Promise<void> {
  await fetch("/api/auth/sign-out", { method: "POST", credentials: "include" });
}

export type HardwareRevision = {
  id: string;
  git_oid: string;
  kicad_tree: string | null;
  has_pcb: number | boolean | null;
  label: string | null;
  parse_artifacts_r2_prefix: string | null;
  created_at: string;
};

export type Artifact = {
  id: string;
  kind: string;
  status: string;
  git_oid: string | null;
  path: string | null;
  r2_key: string | null;
  byte_size: number | null;
  metadata_json: string;
  content_type: string | null;
  created_at: string;
};

export type Job = {
  id: string;
  kind: string;
  kind_detail: string | null;
  state: string;
  check_status?: string;
  checker_version?: string | null;
  error: string | null;
  review_id: string | null;
  artifact_id: string | null;
  r2_prefix?: string | null;
  created_at: string;
  updated_at?: string | null;
  finished_at: string | null;
  started_at?: string | null;
  elapsed_ms?: number | null;
};

export type Issue = {
  id: string;
  number: number | null;
  title: string;
  body: string | null;
  git_ref: string;
  path: string | null;
  workstream: string;
  status: string;
  anchor_kind: string | null;
  anchor_json: string | null;
  created_at: string;
  comment_count?: number;
};

export type IssueComment = {
  id: string;
  project_id: string;
  issue_id: string;
  author_user_id: string;
  body: string;
  created_at: string;
  origin?: "ui" | "think" | null;
  think_tool_call_id?: string | null;
  author_display_name?: string | null;
  author_origin_marker?: "ui" | "think" | null;
};

export type IssueAttachment = {
  id: string;
  path: string | null;
  content_type: string | null;
  byte_size: number | null;
  metadata_json: string;
  created_at: string;
};

export type Review = {
  id: string;
  title: string | null;
  from_ref: string;
  to_ref: string;
  from_oid: string | null;
  to_oid: string | null;
  workstream: string;
  status: string;
  check_results_json: string;
  files_summary_json: string;
  circuit_diff_r2_key: string | null;
  agent_findings_r2_key: string | null;
  created_at: string;
};

export type ReviewComment = {
  id: string;
  review_id: string;
  anchor_kind: string;
  anchor_json: string;
  body: string;
  author_user_id: string;
  created_at: string;
};

export type LibraryRef = {
  library_id: string;
  ref_kind: string;
  git_url: string | null;
  git_oid: string | null;
  notes: string | null;
};

export type ProjectMember = {
  user_id: string;
  role: Role;
  created_at?: string;
  name: string | null;
  email: string | null;
};

export type ProjectAgentNote = {
  title: string;
  description: string;
};

export type ProjectRow = {
  id: string;
  slug: string;
  name: string;
  default_git_remote: string | null;
  default_branch: string;
  forge?: string | null;
  repo?: string | null;
  installation_id?: string | null;
  settings_json: string | Record<string, unknown> | null;
};

export type GithubAppStatus = {
  configured: boolean;
  missing: string[];
  install_url: string | null;
  note: string;
};

export type GithubInstallation = {
  installation_id: string;
  account_login: string;
  account_type: string;
  account_id: string | null;
  org_id: string | null;
  suspended_at: string | null;
  repository_selection: string | null;
};

export type GithubBind = {
  forge: string | null;
  repo: string | null;
  installation_id: string | null;
  default_branch: string;
  default_git_remote: string | null;
};

export type CircuitDiffFile = {
  path: string;
  status: string;
  additions?: number;
  deletions?: number;
  byte_delta?: number;
  pin_retargets?: number;
  patch?: string | null;
};

export type CircuitHighlight = {
  path: string;
  pin_retargets?: number;
  reason: string;
};

export type PairRow = {
  id: string;
  phy_pins: string[];
  from_mag: string[];
  to_mag: string[];
  status: string;
};

export type PairView = {
  kind: string;
  sheet_path: string;
  phy: { refdes: string; value?: string | null };
  magnetics: { refdes: string; value?: string | null };
  pairs: PairRow[];
};

export type PinChange = {
  identity: { sheet_path: string; refdes: string; unit?: number; pin_number: string };
  kind: string;
  from?: { pin_name?: string | null; net_name?: string | null; counterparts?: { refdes: string; pin_number: string; pin_name?: string | null }[] } | null;
  to?: { pin_name?: string | null; net_name?: string | null; counterparts?: { refdes: string; pin_number: string; pin_name?: string | null }[] } | null;
};

export type CircuitDiff = {
  v?: string;
  from?: { ref?: string; oid?: string };
  to?: { ref?: string; oid?: string };
  files?: CircuitDiffFile[];
  summary?: {
    pin_retargets?: number;
    pin_renames?: number;
    pins_added?: number;
    pins_removed?: number;
    power_rails?: { from_names?: string[]; to_names?: string[]; unchanged?: boolean };
  };
  highlights?: CircuitHighlight[];
  pin_changes?: PinChange[];
  pair_views?: PairView[];
  warnings?: string[];
};

export type Requirement = {
  id: string;
  title: string;
  status: string;
  git_path: string | null;
};

export type Milestone = {
  id: string;
  name: string;
  status: string;
  target_date?: string | null;
};

export type OrgRow = {
  id?: string;
  slug: string;
  name: string;
};

export type ProjectHome = {
  org: OrgRow;
  project: { id: string; slug: string; name: string };
  role: Role;
  accessible_projects?: { id: string; slug: string; name: string }[];
  requirements: Requirement[];
  hardware: HardwareRevision[];
  software: unknown[];
  issues: Issue[];
  reviews: Review[];
  milestones: Milestone[];
  jobs: Job[];
};

function p(org: string, project: string, tail = ""): string {
  const base = `/api/o/${encodeURIComponent(org)}/p/${encodeURIComponent(project)}`;
  return tail ? `${base}/${tail}` : base;
}

async function readError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
  return body.message || body.error || `${res.status}`;
}

export async function apiGet<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init }); // session cookie, same-origin Worker
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return (await res.json()) as T;
}

export async function apiJson<T>(url: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function apiUpload<T>(url: string, file: File, field = "file"): Promise<T> {
  const form = new FormData();
  form.set(field, file);
  const res = await fetch(url, { method: "POST", credentials: "include", body: form });
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return (await res.json()) as T;
}

export function parseSettingsJson(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }
  return {};
}

/** Seed already has fake hw oids; live ingest UI is gated on this flag. */
export function hwCollabEnabled(settings: unknown): boolean {
  return parseSettingsJson(settings).hw_collab_loop === true;
}

export function parseJsonField<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function artifactMeta(raw: string | null | undefined): {
  label?: string;
  filename?: string;
  files?: string[];
  kind_detail?: string;
  kicad_tree?: string;
} {
  return parseJsonField(raw, {});
}

export function revisionHasPcb(rev: { has_pcb?: number | boolean | null }): boolean {
  return rev.has_pcb === 1 || rev.has_pcb === true;
}

/** Dispatch key (`kind_detail` or SQL kind). Labels live in `jobTypeLabel`. */
export function jobType(kind: string, kindDetail?: string | null): string {
  return (kindDetail && kindDetail.trim()) || kind;
}

export function shortOid(oid: string | null | undefined, n = 7): string {
  if (!oid) return "—";
  return oid.length > n ? oid.slice(0, n) : oid;
}

export function artifactUrl(org: string, project: string, id: string): string {
  return p(org, project, `artifacts/${encodeURIComponent(id)}`);
}


export function circuitDiffUrl(org: string, project: string, reviewId: string): string {
  return p(org, project, `reviews/${encodeURIComponent(reviewId)}/circuit-diff`);
}

export function findingsUrl(org: string, project: string, reviewId: string): string {
  return p(org, project, `reviews/${encodeURIComponent(reviewId)}/findings`);
}

export async function getOrg(org: string) {
  return apiGet<{
    org: OrgRow;
    projects: { id: string; slug: string; name: string }[];
    members: Pick<ProjectMember, "user_id" | "role" | "name" | "email" | "created_at">[];
  }>(`/api/o/${encodeURIComponent(org)}`);
}

/** POST /api/o/:org/projects — platform self-host; GitHub remote is not required. */
export async function createProject(org: string, body: { name: string; slug: string }) {
  return apiJson<{ id: string; slug: string }>(`/api/o/${encodeURIComponent(org)}/projects`, "POST", body);
}

export async function getProject(org: string, project: string) {
  return apiGet<{ org: OrgRow; project: ProjectRow; role: Role }>(p(org, project));
}

export async function getProjectHome(org: string, project: string) {
  return apiGet<ProjectHome>(p(org, project, "home"));
}

export async function listRequirements(org: string, project: string) {
  return apiGet<{ items: Requirement[] }>(p(org, project, "requirements"));
}

export async function getHardware(org: string, project: string) {
  return apiGet<{ revisions: HardwareRevision[]; artifacts: Artifact[] }>(p(org, project, "hardware"));
}

export async function uploadHardwareZip(org: string, project: string, file: File) {
  return apiUpload<{
    artifact_id: string;
    job_id: string;
    job_state: string;
    label?: string;
    error?: string | null;
  }>(p(org, project, "hardware/upload"), file);
}

export type StagedHardwareZip =
  | { ok: true; artifact_id: string; path_count: number }
  | { ok: false; status: number; message: string; artifact_id?: string; path_count?: number };

export type StageHardwareZipProgress = { loaded: number; total: number };

function readStagedBody(raw: string): {
  artifact_id?: string;
  path_count?: number;
  message?: string;
  error?: string;
} {
  try {
    return JSON.parse(raw) as {
      artifact_id?: string;
      path_count?: number;
      message?: string;
      error?: string;
    };
  } catch {
    return {};
  }
}

/** Staging-only. Does not enqueue import — Think decides later. Real XHR progress. */
export async function stageHardwareZip(
  org: string,
  project: string,
  file: File,
  opts?: { onProgress?: (progress: StageHardwareZipProgress) => void },
): Promise<StagedHardwareZip> {
  const form = new FormData();
  form.set("file", file);
  const url = p(org, project, "hardware/uploads");
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable && event.total > 0 ? event.total : file.size;
      opts?.onProgress?.({ loaded: event.loaded, total });
    };
    xhr.onload = () => {
      const body = readStagedBody(xhr.responseText);
      if (xhr.status === 201 && typeof body.artifact_id === "string") {
        resolve({
          ok: true,
          artifact_id: body.artifact_id,
          path_count: typeof body.path_count === "number" ? body.path_count : 0,
        });
        return;
      }
      if (xhr.status === 409 && typeof body.artifact_id === "string") {
        resolve({
          ok: false,
          status: 409,
          message: body.message || body.error || "already uploaded",
          artifact_id: body.artifact_id,
          path_count: typeof body.path_count === "number" ? body.path_count : undefined,
        });
        return;
      }
      resolve({
        ok: false,
        status: xhr.status,
        message: body.message || body.error || `${xhr.status}`,
        artifact_id: typeof body.artifact_id === "string" ? body.artifact_id : undefined,
      });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, message: "upload failed" });
    xhr.send(form);
  });
}

export async function getArtifactTree(org: string, project: string, artifactId: string) {
  return apiGet<{ artifact_id: string; paths: string[] }>(
    p(org, project, `hardware/artifacts/${encodeURIComponent(artifactId)}/tree`),
  );
}

export type RevisionFileCatalogRow = { path: string; byte_size: number | null };

export async function getRevisionFileCatalog(org: string, project: string, revId: string) {
  return apiGet<{
    revision_id: string;
    git_oid: string;
    files: RevisionFileCatalogRow[];
    truncated: boolean;
  }>(p(org, project, `hardware/revisions/${encodeURIComponent(revId)}/catalog`));
}

export async function listJobs(org: string, project: string) {
  return apiGet<{ items: Job[] }>(p(org, project, "jobs"));
}

export async function getJob(org: string, project: string, jobId: string) {
  return apiGet<Job>(p(org, project, `jobs/${encodeURIComponent(jobId)}`));
}

export async function enqueueJob(
  org: string,
  project: string,
  body: { kind: string; kind_detail?: string | null; review_id?: string | null; artifact_id?: string | null },
) {
  return apiJson<Job>(p(org, project, "jobs"), "POST", body);
}

export async function cancelJob(org: string, project: string, jobId: string) {
  return apiJson<Job>(p(org, project, `jobs/${encodeURIComponent(jobId)}/cancel`), "POST", {});
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("aborted", "AbortError"));
      return;
    }
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

const TERMINAL = new Set(["succeeded", "failed", "cancelled", "passed", "timed_out", "inconclusive"]);

/** Isolate never runs rsci — poll until the checked-in runner completes. */
export async function pollJob(
  org: string,
  project: string,
  jobId: string,
  signal?: AbortSignal,
  intervalMs = 2000,
  timeoutMs = 5 * 60 * 1000,
): Promise<Job> {
  const start = Date.now();
  let last: Job | null = null;
  while (Date.now() - start < timeoutMs) {
    if (signal?.aborted) throw new DOMException("aborted", "AbortError");
    last = await getJob(org, project, jobId);
    if (TERMINAL.has(last.state) || (last.check_status && TERMINAL.has(last.check_status))) return last;
    await sleep(intervalMs, signal);
  }
  return last ?? (await getJob(org, project, jobId));
}

export async function listIssues(org: string, project: string) {
  return apiGet<{ items: Issue[] }>(p(org, project, "issues"));
}

export async function getIssue(org: string, project: string, issueId: string) {
  return apiGet<{ issue: Issue; attachments: IssueAttachment[]; comments: IssueComment[] }>(
    p(org, project, `issues/${encodeURIComponent(issueId)}`),
  );
}

export async function createIssue(
  org: string,
  project: string,
  body: {
    title: string;
    git_ref: string;
    path?: string;
    body?: string;
    workstream?: string;
    anchor_kind?: string;
    anchor_json?: Record<string, unknown>;
  },
) {
  return apiJson<{ id: string }>(p(org, project, "issues"), "POST", body);
}

export async function uploadIssueScreenshot(org: string, project: string, issueId: string, file: File) {
  return apiUpload<{ id: string }>(p(org, project, `issues/${encodeURIComponent(issueId)}/attachments`), file);
}

export async function listReviews(org: string, project: string) {
  return apiGet<{ items: Review[] }>(p(org, project, "reviews"));
}

export async function getReview(org: string, project: string, reviewId: string) {
  return apiGet<{
    review: Review;
    comments: ReviewComment[];
    jobs: Job[];
    circuit_diff_artifact_id: string | null;
  }>(p(org, project, `reviews/${encodeURIComponent(reviewId)}`));
}

export async function createReview(
  org: string,
  project: string,
  body: { from_ref: string; to_ref: string; from_oid?: string; to_oid?: string; title?: string; workstream?: string },
) {
  return apiJson<{ id: string; job_id: string | null; job_state: string | null }>(p(org, project, "reviews"), "POST", body);
}

export async function postReviewComment(
  org: string,
  project: string,
  reviewId: string,
  body: { body: string; anchor_kind?: string; anchor_json?: Record<string, unknown> },
) {
  return apiJson<{ id: string }>(p(org, project, `reviews/${encodeURIComponent(reviewId)}/comments`), "POST", body);
}

export type MindSceneEmpty = {
  error: string;
  message: string;
  has_design_mind?: boolean;
  git_oid?: string | null;
};

/** GET …/mind/scene — live mind-scene/1.1 JSON, or 200 empty no_mind_scene (never DEMO). */
export async function getMindScene(
  org: string,
  project: string,
): Promise<
  | { ok: true; status: number; scene: unknown }
  | { ok: false; status: number; error: string; message: string; has_design_mind?: boolean; git_oid?: string | null }
> {
  const res = await fetch(p(org, project, "mind/scene"), { credentials: "include" });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.ok && body.error === "no_mind_scene") {
    return {
      ok: false,
      status: res.status,
      error: "no_mind_scene",
      message: typeof body.message === "string" ? body.message : "No mind scene yet",
      has_design_mind: body.has_design_mind === true,
      git_oid: typeof body.git_oid === "string" ? body.git_oid : body.git_oid === null ? null : undefined,
    };
  }
  if (res.ok) return { ok: true, status: res.status, scene: body };
  const error = typeof body.error === "string" ? body.error : `http_${res.status}`;
  const message =
    typeof body.message === "string"
      ? body.message
      : error === "no_mind_scene"
        ? "No mind scene yet"
        : `mind scene ${res.status}`;
  return {
    ok: false,
    status: res.status,
    error,
    message,
    has_design_mind: body.has_design_mind === true,
    git_oid: typeof body.git_oid === "string" ? body.git_oid : body.git_oid === null ? null : undefined,
  };
}

export async function listMaterials(org: string, project: string) {
  return apiGet<{ items: MaterialRow[] }>(p(org, project, "materials"));
}

/** Multipart `file` + `kind` (+ optional `mpn`). Preferred over JSON for PDFs. */
export async function uploadMaterial(
  org: string,
  project: string,
  file: File,
  opts: { kind: MaterialKind; mpn?: string },
): Promise<MaterialUploadResult> {
  const form = new FormData();
  form.set("file", file);
  form.set("kind", opts.kind);
  if (opts.mpn?.trim()) form.set("mpn", opts.mpn.trim());
  const res = await fetch(p(org, project, "materials"), {
    method: "POST",
    credentials: "include",
    body: form,
  });
  const body = (await res.json().catch(() => ({}))) as MaterialUploadResult;
  if (!res.ok) {
    const message = body.message || body.error || `${res.status}`;
    throw new ApiError(res.status, message);
  }
  return body;
}

/** JSON `content` / `content_base64` — small text notes only. */
export async function uploadMaterialContent(
  org: string,
  project: string,
  body: {
    filename: string;
    kind: MaterialKind;
    content?: string;
    content_base64?: string;
    mpn?: string;
    content_type?: string;
  },
) {
  return apiJson<MaterialUploadResult>(p(org, project, "materials"), "POST", body);
}

export async function searchMaterials(
  org: string,
  project: string,
  query: string,
  maxNumResults?: number,
) {
  const payload: { query: string; max_num_results?: number } = { query };
  if (maxNumResults != null) payload.max_num_results = maxNumResults;
  return apiJson<unknown>(p(org, project, "materials/search"), "POST", payload);
}

export async function getCircuitDiff(org: string, project: string, reviewId: string): Promise<CircuitDiff | null> {
  const res = await fetch(circuitDiffUrl(org, project, reviewId), { credentials: "include" });
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return (await res.json()) as CircuitDiff;
}

export async function getFindings(org: string, project: string, reviewId: string): Promise<FindingsEnvelope> {
  const res = await fetch(findingsUrl(org, project, reviewId), { credentials: "include" });
  if (res.status === 404) return { summary: null, findings: [] };
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return parseFindingsEnvelope((await res.json()) as unknown);
}

export async function getSettings(org: string, project: string) {
  return apiGet<{
    project: ProjectRow;
    members: ProjectMember[];
    project_agent: ProjectAgentNote | null;
    libraries: LibraryRef[];
    role: Role;
  }>(p(org, project, "settings"));
}

export async function patchSettings(
  org: string,
  project: string,
  body: {
    hw_collab_loop?: boolean;
    default_git_remote?: string;
    default_branch?: string;
    settings_json?: Record<string, unknown>;
  },
) {
  return apiJson<{ ok: boolean; settings_json: Record<string, unknown> }>(p(org, project, "settings"), "PATCH", body);
}

export type ProjectInvite = {
  id: string;
  project_id: string;
  capability: InviteCapability;
  role: InviteRole;
  email: string | null;
  invited_by: string;
  audit_id: string;
  expires_at: string;
  revoked_at: string | null;
  redeemed_at: string | null;
  redeemed_by: string | null;
  created_at: string;
  token?: string;
};

export type RedeemInviteResult = {
  ok: true;
  already_member: boolean;
  project_id: string;
  role: InviteRole;
  capability: InviteCapability;
  member_role: Role;
  invite_id: string;
};

export async function listProjectInvites(org: string, project: string) {
  return apiGet<{ items: ProjectInvite[] }>(p(org, project, "invites"));
}

export async function createProjectInvite(
  org: string,
  project: string,
  body: { capability: InviteCapability; email?: string; expires_in_hours?: number },
) {
  return apiJson<ProjectInvite>(p(org, project, "invites"), "POST", body);
}

export async function revokeProjectInvite(org: string, project: string, inviteId: string) {
  return apiJson<{ ok: true; invite: ProjectInvite }>(p(org, project, `invites/${inviteId}/revoke`), "POST");
}

export async function redeemProjectInvite(token: string) {
  return apiJson<RedeemInviteResult>("/api/invites/redeem", "POST", { token });
}

export async function getGithubAppStatus() {
  return apiGet<GithubAppStatus>("/api/github/app/status");
}

export async function listGithubInstallations() {
  return apiGet<{ app_configured: boolean; items: GithubInstallation[] }>("/api/github/installations");
}

export async function getProjectGithub(org: string, project: string) {
  return apiGet<{ app: GithubAppStatus; bind: GithubBind }>(p(org, project, "github"));
}

export async function bindGithubRepo(
  org: string,
  project: string,
  body: { installation_id: string; repo: string; default_branch?: string },
) {
  return apiJson<{ ok: boolean; bind: GithubBind }>(p(org, project, "github/bind"), "POST", body);
}

export async function unbindGithubRepo(org: string, project: string) {
  return apiJson<{ ok: boolean; unbound: boolean }>(p(org, project, "github/bind"), "DELETE");
}

export async function ingestGithubRepo(org: string, project: string, body?: { ref?: string }) {
  return apiJson<{
    ok: boolean;
    artifact_id: string;
    job_id: string;
    job_state: string;
    r2_key: string;
    label: string;
    path_count: number;
    ref: string;
    repo: string;
    error: string | null;
  }>(p(org, project, "github/ingest"), "POST", body ?? {});
}

export { basenamePath, fileUrl, isKicadPcb, isKicadSch } from "./kicad-path.ts";

/** Tree lists every revision file. Viewer (not the tree) gates KiCad types. */
export function filterTree(paths: string[], _hasPcb?: boolean): string[] {
  return paths.filter((p) => p.replaceAll("\\", "/").replace(/^\/+/, ""));
}

function decodeRevisionTreeToken(path: string): string {
  let current = path.replaceAll("\\", "/").replace(/^\/+/, "");
  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(current);
      if (next === current) break;
      current = next;
    } catch {
      break;
    }
  }
  return current;
}

/**
 * Deep-link viewer path. Do not wait for GET …/artifacts/:id/tree
 * (that handler used to arrayBuffer() the whole zip — QA r19 +30s before
 * the first revision-file GET). Official listing is metadata.files or a
 * ranged CD read; `?file=` is enough to start FetchFileSystem.
 * Only mark missing after the zip listing settles (ingest metadata is
 * schematic-only and may omit the deep-linked sheet).
 *
 * Missing is path-in-tree after listingComplete — not a KiCanvas extension
 * gate. Viewer-type checks belong at the open site, not here.
 */
export function filesViewerPath(opts: {
  selectedPath: string | null | undefined;
  tree: string[];
  treeReady: boolean;
  treeRevId: string | null;
  activeRevId: string | null;
  listingComplete: boolean;
  pathInTree: (path: string, tree: string[]) => boolean;
}): { path: string | null; missing: boolean } {
  const selected = typeof opts.selectedPath === "string" ? opts.selectedPath.trim() : "";
  if (!selected) return { path: null, missing: false };
  if (opts.activeRevId && opts.treeRevId && opts.treeRevId !== opts.activeRevId) {
    return { path: null, missing: false };
  }
  if (opts.pathInTree(selected, opts.tree)) {
    return { path: selectionForRevisionTree(selected, opts.tree), missing: false };
  }
  if (!opts.listingComplete) {
    return { path: selected, missing: false };
  }
  return { path: selected, missing: true };
}

/** Keep `?file=` until the revision tree is loaded; then resolve the canonical tree path. */
export function selectionForRevisionTree(selected: string | null | undefined, tree: string[]): string | null {
  if (!selected) return null;
  const want = decodeRevisionTreeToken(selected);
  if (tree.length === 0) return want;
  const hit = tree.find((entry) => decodeRevisionTreeToken(entry) === want);
  return hit ? decodeRevisionTreeToken(hit) : null;
}

export function findImportSvg(artifacts: Artifact[], oid: string | null | undefined): Artifact | undefined {
  if (!oid) return undefined;
  const matches = artifacts.filter(
    (a) => a.kind === "import_svg" && a.git_oid && (a.git_oid === oid || a.git_oid.startsWith(oid) || oid.startsWith(a.git_oid)),
  );
  return matches.find((a) => (a.path || "").includes("schematic.svg")) ?? matches[0];
}

export function filesForRevision(artifacts: Artifact[], rev: HardwareRevision): string[] {
  const zip = artifacts.find((a) => a.kind === "kicad_zip" && a.git_oid && a.git_oid === rev.git_oid);
  const files = zip ? artifactMeta(zip.metadata_json).files : undefined;
  if (files?.length) return files;
  return [];
}

/** Ingest metadata is schematic-only; zip listing still has .kicad_pcb / .kicad_pro. */
export function mergeRevisionFileTree(...lists: Array<readonly string[] | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const list of lists) {
    if (!list) continue;
    for (const raw of list) {
      const path = raw.replaceAll("\\", "/").replace(/^\/+/, "").trim();
      if (!path || seen.has(path)) continue;
      seen.add(path);
      out.push(path);
    }
  }
  return out;
}

export const HARDWARE_RELOAD_EVENT = "pcbx:reload-hardware";
